
import React, { useState } from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { MoreVertical, Eye, EyeOff, ArchiveIcon, ArchiveRestore, Trash2 } from "lucide-react";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import { PriorityBadge } from "@/components/notifications/StatusBadges";
import ChatStatusIndicator from "@/components/notifications/ChatStatusIndicator";
import { cn } from "@/lib/utils";
import { useNotificationReadStatus } from "@/hooks/useNotificationReadStatus";
import { SupabaseNotification } from "@/types/notifications";
import StatusChangeDropdown from "../list/StatusChangeDropdown";
import { StockStatusBadge } from "../StockStatusBadge";
import { isAwaitingStockStatus } from "@/utils/statusMappings";
import { StockAlertDialog } from "../StockAlertDialog";

interface MobileNotificationCardProps {
  notification: SupabaseNotification;
  formatDate: (date: string) => string;
  onNotificationClick: (id: string) => void;
  onArchive: (e: React.MouseEvent, id: string) => void;
  onRestore: (e: React.MouseEvent, id: string) => void;
  onDelete: (e: React.MouseEvent, id: string) => void;
  selectedStatuses: string[];
  viewMode: "active" | "archived";
}

const MobileNotificationCard = ({
  notification,
  formatDate,
  onNotificationClick,
  onArchive,
  onRestore,
  onDelete,
  selectedStatuses,
  viewMode
}: MobileNotificationCardProps) => {
  const { markAsRead, markAsUnread, isNotificationRead } = useNotificationReadStatus();
  const isRead = isNotificationRead(notification.id);
  const isHighPriority = notification.priority === 'hoog';
  const [showStockDialog, setShowStockDialog] = useState(false);
  
  const handleMarkAsRead = (e: React.MouseEvent) => {
    e.stopPropagation();
    markAsRead.mutate(notification.id);
  };

  const handleMarkAsUnread = (e: React.MouseEvent) => {
    e.stopPropagation();
    markAsUnread.mutate(notification.id);
  };

  const handleStatusClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent notification card click
  };


  return (
    <Card 
      className={cn(
        "cursor-pointer hover:bg-slate-50 relative",
        !isRead && "bg-blue-50 font-medium",
        isHighPriority && "border-l-4 border-l-red-500"
      )}
      onClick={() => onNotificationClick(notification.id)}
    >
      <CardHeader className="flex flex-col space-y-2 pb-2">
        {isAwaitingStockStatus(notification.status) && (
          <div className="flex justify-end -mt-2 -mr-2">
            <StockStatusBadge
              notificationId={notification.id}
              onClick={() => setShowStockDialog(true)}
            />
          </div>
        )}
        <div className="flex flex-row items-center justify-between space-y-0">
          <div className="text-sm font-medium capitalize">{notification.type}</div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={(e) => e.stopPropagation()}
            >
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem onClick={() => onNotificationClick(notification.id)}>
              <Eye className="h-4 w-4 mr-2" />
              <span>Details</span>
            </DropdownMenuItem>
            
            <DropdownMenuSeparator />
            
            {isRead ? (
              <DropdownMenuItem onClick={handleMarkAsUnread}>
                <EyeOff className="h-4 w-4 mr-2" />
                <span>Markeren als ongelezen</span>
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem onClick={handleMarkAsRead}>
                <Eye className="h-4 w-4 mr-2" />
                <span>Markeren als gelezen</span>
              </DropdownMenuItem>
            )}
            
            <DropdownMenuSeparator />
            
            {viewMode === "archived" ? (
              <DropdownMenuItem onClick={(e) => onRestore(e, notification.id)}>
                <ArchiveRestore className="h-4 w-4 mr-2" />
                <span>Herstellen</span>
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem onClick={(e) => onArchive(e, notification.id)}>
                <ArchiveIcon className="h-4 w-4 mr-2" />
                <span>Archiveren</span>
              </DropdownMenuItem>
            )}
            
            <DropdownMenuItem 
              className="text-red-600 focus:text-red-600" 
              onClick={(e) => onDelete(e, notification.id)}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              <span>Verwijderen</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        </div>
      </CardHeader>
      <CardContent className="pl-2 pb-2 pt-0">
        <div className="text-sm font-medium">
          {notification.vo_number ? `VO-${notification.vo_number}` : '-'}
        </div>
        <div className="text-sm text-muted-foreground">
          {notification.message}
        </div>
        <div className="flex items-center justify-between mt-2">
          <div className="flex items-center gap-2">
            <div className="relative z-10">
              <StatusChangeDropdown 
                notification={notification} 
                onClick={handleStatusClick}
              />
            </div>
            <ChatStatusIndicator notificationId={notification.id} />
          </div>
          <PriorityBadge priority={notification.priority} />
        </div>
        <div className="text-xs text-muted-foreground mt-2">
          {formatDate(notification.created_at)}
        </div>
      </CardContent>
      
      <StockAlertDialog
        notificationId={notification.id}
        voNumber={notification.vo_number}
        open={showStockDialog}
        onOpenChange={setShowStockDialog}
      />
    </Card>
  );
};

export default MobileNotificationCard;
